/**
 * useAnalyticsWorkspace Hook
 * 
 * Fetches workspace creation date and default currency for Analytics page.
 * Handles loading state and error handling.
 */

import { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import {
  doc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { WorkspaceSettings } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseAnalyticsWorkspaceParams {
  workspaceId: string;
}

export interface UseAnalyticsWorkspaceResult {
  workspaceCreatedAt: Date | null;
  defaultCurrency: string;
  defaultCurrencyLoaded: boolean;
  setWorkspaceCreatedAt: React.Dispatch<React.SetStateAction<Date | null>>;
  setDefaultCurrency: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Hook for fetching workspace data for Analytics
 */
export const useAnalyticsWorkspace = ({
  workspaceId,
}: UseAnalyticsWorkspaceParams): UseAnalyticsWorkspaceResult => {
  const [workspaceCreatedAt, setWorkspaceCreatedAt] = useState<Date | null>(null);
  // Don't initialize with "USD" - wait until we load the actual default currency
  // This prevents showing wrong currency values during initialization
  const [defaultCurrency, setDefaultCurrency] = useState<string>("");
  const [defaultCurrencyLoaded, setDefaultCurrencyLoaded] = useState<boolean>(false);

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    const workspaceRef = doc(db, "workspaces", workspaceId);
    const unsubscribeWorkspace = onSnapshot(workspaceRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Get workspace creation date
        if (data.createdAt) {
          let createdAtDate: Date;
          if (typeof (data.createdAt as Timestamp).toDate === "function") {
            createdAtDate = (data.createdAt as Timestamp).toDate();
          } else if (data.createdAt instanceof Date) {
            createdAtDate = data.createdAt;
          } else if (typeof data.createdAt === "number") {
            createdAtDate = new Date(data.createdAt);
          } else if (data.createdAt && typeof data.createdAt === "object" && "seconds" in data.createdAt) {
            const timestampObj = data.createdAt as { seconds: number };
            createdAtDate = new Date(timestampObj.seconds * 1000);
          } else {
            createdAtDate = new Date();
          }
          setWorkspaceCreatedAt(createdAtDate);
        }
      }
    });

    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettings;
        const newDefaultCurrency = data.defaultCurrency || "USD";
        console.log('[useAnalyticsWorkspace] Default currency loaded', {
          oldDefaultCurrency: defaultCurrency,
          newDefaultCurrency,
          timestamp: new Date().toISOString()
        });
        setDefaultCurrency(newDefaultCurrency);
        setDefaultCurrencyLoaded(true);
      } else {
        // Settings don't exist yet, use USD as default but mark as loaded
        console.log('[useAnalyticsWorkspace] Settings not found, using USD default', {
          timestamp: new Date().toISOString()
        });
        setDefaultCurrency("USD");
        setDefaultCurrencyLoaded(true);
      }
    });

    return () => {
      unsubscribeWorkspace();
      unsubscribeSettings();
    };
  }, [workspaceId]);

  return {
    workspaceCreatedAt,
    defaultCurrency,
    defaultCurrencyLoaded,
    setWorkspaceCreatedAt,
    setDefaultCurrency,
  };
};
